import { CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import { useModalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';

import type { TrustpilotBusinessUnit, TrustpilotInfo } from '../types';

type Props = {
  workspace: TrustpilotInfo;
  onBusinessUnitsUpdated: (units: TrustpilotBusinessUnit[]) => void;
};

type UnitProps = {
  unit: TrustpilotBusinessUnit;
  onCancelClick: () => void;
};

const Unit = ({ unit, onCancelClick }: UnitProps) => {
  return (
    <UnitStyle>
      <Flex j="space-between">
        <VFlex g={6}>
          <Typo type="label2">{unit.display_name}</Typo>
          <Typo type="label3">{unit.identifying}</Typo>
        </VFlex>
        <Flex a="center">
          <IconButton icon={<CloseOutlined />} onClick={onCancelClick} />
        </Flex>
      </Flex>
    </UnitStyle>
  );
};

const TrustpilotSearchModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const [keyword, setKeyword] = useState('');
  const [selectedUnits, setSelectedUnits] = useState<TrustpilotBusinessUnit[]>(
    props.workspace.business_units || [],
  );
  const fetchBusinessUnit = useCallback(
    async (keyword: string) => {
      if (!keyword) return [];
      const units = await service.trustpilot.searchBusinessUnit(
        props.workspace.alias,
        keyword,
      );
      return units;
    },
    [props.workspace.alias],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['trustpilot-business-unit', keyword],
    queryFn: () => fetchBusinessUnit(keyword),
  });

  const options = useMemo(() => {
    if (!data) return [];
    return data?.map((a) => {
      return {
        value: a.id,
        label: (
          <VFlex g={6}>
            <Typo type="label2">{a.display_name}</Typo>
            <Typo type="label3">{a.name.identifying}</Typo>
          </VFlex>
        ),
        searchValue: `${a.display_name} ${a.name.identifying}`,
      };
    });
  }, [data]);

  const debouncedSetkeyword = useDebouncedCallback(
    (search: string) => setKeyword(search),
    500,
  );

  const isDisabled = () => {
    if (
      selectedUnits?.length === 0 &&
      (!props.workspace.business_units ||
        props.workspace.business_units?.length === 0)
    ) {
      return true;
    }

    if (selectedUnits?.length !== props.workspace.business_units?.length) {
      return false;
    }

    const set = new Set<string>();
    for (const unit of selectedUnits) {
      set.add(unit.id);
    }

    for (const unit of props.workspace.business_units || []) {
      if (!set.has(unit.id)) {
        return false;
      }
    }

    return true;
  };

  const onSaveChangesClick = async () => {
    await service.trustpilot.setBusinessUnit(
      props.workspace.alias,
      selectedUnits.map((u) => u.id),
    );
    props.onBusinessUnitsUpdated(selectedUnits);

    closeModal();
  };

  return (
    <ModalStyle>
      <Typo type="label0" weight="sb">
        Manage business unit
      </Typo>
      <Space h={8} />
      <Typo type="label2">
        New reviews will be ingested to Syncly every hour.
      </Typo>
      <Space h={32} />
      <Typo type="label2" weight="sb" className="text-gray-800">
        Add business unit
      </Typo>
      <Space h={8} />
      <Select
        showSearch
        size="large"
        placeholder="Search business unit"
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode as HTMLDivElement;
        }}
        optionFilterProp="searchValue"
        options={options}
        loading={isLoading}
        onSearch={debouncedSetkeyword}
        onSelect={(v: string) => {
          const currUnit = data?.find((a) => a.id === v);
          if (currUnit) {
            setSelectedUnits(
              selectedUnits.concat({
                id: v,
                display_name: currUnit.display_name,
                identifying: currUnit.name.identifying,
              }),
            );
          }
        }}
      />
      <Space h={32} />
      <Typo type="label2" weight="sb">
        Added units
      </Typo>
      <Space h={8} />
      <VFlex g={8}>
        {selectedUnits.map((u) => (
          <Unit
            key={u.id}
            unit={u}
            onCancelClick={() => {
              setSelectedUnits(selectedUnits.filter((s) => s.id !== u.id));
            }}
          />
        ))}
      </VFlex>
      <Space h={40} />
      <Flex j="flex-end" g={12}>
        <Button variant="outlined" sizeType="m" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          sizeType="m"
          onClick={onSaveChangesClick}
          disabled={isDisabled()}
        >
          Save changes
        </Button>
      </Flex>
    </ModalStyle>
  );
};

const UnitStyle = styled(VFlex)`
  padding: 8px 10px;
  background-color: ${(props) => props.theme.colors.gray200};
  height: 50px;
`;

const ModalStyle = styled(VFlex)`
  width: 620px;
  padding: 32px;
  ${modalStyle};
`;

export default TrustpilotSearchModal;
